import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, Receipt, Save, Send, Euro, Calendar, Briefcase,
  CheckCircle, Info, Building2, User, Upload, FileText
} from 'lucide-react';
import {
  getFilteredAffaires, getFilteredFactures,
  getClientById, getIngenieurById, getSocieteById
} from '../data/mockData';
import { useFilters } from '../App';

const FactureForm = () => {
  const filters = useFilters();
  const [selectedAffaireId, setSelectedAffaireId] = useState('');
  const [typeFacture, setTypeFacture] = useState('acompte');
  const [pourcentage, setPourcentage] = useState(50);
  const [numeroFacture, setNumeroFacture] = useState('');
  const [dateEnvoi, setDateEnvoi] = useState(new Date().toISOString().split('T')[0]);
  const [fichierFacture, setFichierFacture] = useState(null);

  const allAffaires = getFilteredAffaires(filters);
  const allFactures = getFilteredFactures(filters);

  const formatMontant = (montant) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0 }).format(montant);
  };

  const selectedAffaire = allAffaires.find(a => a.id === parseInt(selectedAffaireId));
  const client = selectedAffaire ? getClientById(selectedAffaire.clientId) : null;
  const ingenieur = selectedAffaire ? getIngenieurById(selectedAffaire.ingenieurId) : null;
  const societe = selectedAffaire ? getSocieteById(selectedAffaire.societeId) : null;

  // Factures déjà émises pour cette affaire
  const facturesExistantes = selectedAffaire
    ? allFactures.filter(f => f.affaireId === selectedAffaire.id && f.statut !== 'brouillon')
    : [];
  const montantDejaFacture = facturesExistantes.reduce((acc, f) => acc + f.montantHT, 0);
  const pourcentageDejaFacture = selectedAffaire ? Math.round((montantDejaFacture / selectedAffaire.montantHT) * 100) : 0;

  const montantFacture = selectedAffaire ? Math.round(selectedAffaire.montantHT * pourcentage / 100) : 0;
  const montantTTC = Math.round(montantFacture * 1.2);

  return (
    <div className="page-container">
      <Link to="/facturation" className="btn-back">
        <ArrowLeft size={18} /> Retour à la facturation
      </Link>

      <div className="page-header">
        <div>
          <h1><Receipt size={28} /> Ajouter une facture</h1>
          <div className="page-meta">
            <span>Enregistrer une facture déjà établie dans un outil externe</span>
          </div>
        </div>
      </div>

      <div className="inscription-form">
        <div className="form-card">
          {/* Sélection de l'affaire */}
          <div className="form-section">
            <h2><Briefcase size={20} /> Affaire concernée</h2>
            <div className="form-group">
              <label>Sélectionner l'affaire</label>
              <select
                value={selectedAffaireId}
                onChange={(e) => setSelectedAffaireId(e.target.value)}
                style={{ fontSize: '0.9375rem' }}
              >
                <option value="">— Choisir une affaire —</option>
                {allAffaires.filter(a => a.statut === 'en_cours').map(a => {
                  const cl = getClientById(a.clientId);
                  const soc = getSocieteById(a.societeId);
                  return (
                    <option key={a.id} value={a.id}>
                      [{soc?.nom}] {a.numero} — {cl?.nom} — {a.designation} ({formatMontant(a.montantHT)})
                    </option>
                  );
                })}
              </select>
            </div>

            {selectedAffaire && (
              <div style={{
                display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem',
                padding: '1.25rem', background: 'var(--gray-50)', borderRadius: 'var(--radius)', marginTop: '0.5rem'
              }}>
                <div>
                  <p style={{ fontSize: '0.75rem', color: 'var(--gray-500)', margin: '0 0 0.25rem', textTransform: 'uppercase' }}>Client</p>
                  <p style={{ margin: 0, fontWeight: 600 }}>{client?.nom}</p>
                  <p style={{ margin: 0, fontSize: '0.8125rem', color: 'var(--gray-600)' }}>
                    {client?.marchePublic ? 'Marché public' : 'Client privé'}
                  </p>
                </div>
                <div>
                  <p style={{ fontSize: '0.75rem', color: 'var(--gray-500)', margin: '0 0 0.25rem', textTransform: 'uppercase' }}>Ingénieur</p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <div className="user-avatar user-avatar-xs" style={{ background: ingenieur?.couleur }}>{ingenieur?.initiales}</div>
                    <span style={{ fontWeight: 500 }}>{ingenieur?.prenom} {ingenieur?.nom}</span>
                  </div>
                </div>
                <div>
                  <p style={{ fontSize: '0.75rem', color: 'var(--gray-500)', margin: '0 0 0.25rem', textTransform: 'uppercase' }}>Société</p>
                  <span className="badge" style={{
                    background: societe?.id === 1 ? '#DBEAFE' : '#FFF7ED',
                    color: societe?.id === 1 ? '#1E40AF' : '#9A3412',
                  }}>
                    <Building2 size={12} style={{ marginRight: '0.25rem' }} />{societe?.nom}
                  </span>
                </div>
                <div>
                  <p style={{ fontSize: '0.75rem', color: 'var(--gray-500)', margin: '0 0 0.25rem', textTransform: 'uppercase' }}>Montant total HT</p>
                  <p style={{ margin: 0, fontWeight: 700, color: 'var(--primary)', fontSize: '1.125rem' }}>{formatMontant(selectedAffaire.montantHT)}</p>
                </div>
              </div>
            )}

            {/* Historique facturation */}
            {selectedAffaire && facturesExistantes.length > 0 && (
              <div style={{ marginTop: '1rem' }}>
                <h4 style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem' }}>Factures précédentes</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {facturesExistantes.map(f => (
                    <div key={f.id} style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      padding: '0.5rem 0.75rem', background: 'white', borderRadius: 'var(--radius-sm)',
                      border: '1px solid var(--gray-200)'
                    }}>
                      <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.8125rem' }}>{f.numero}</span>
                      <span className="badge badge-secondary">{f.type === 'acompte' ? 'Acompte' : f.type === 'intermediaire' ? 'Intermédiaire' : 'Solde'}</span>
                      <span style={{ fontWeight: 600 }}>{formatMontant(f.montantHT)}</span>
                      <span className={`badge ${f.statut === 'payee' ? 'badge-success' : f.statut === 'envoyee' ? 'badge-primary' : 'badge-danger'}`}>
                        {f.statut === 'payee' ? 'Payée' : f.statut === 'envoyee' ? 'Envoyée' : 'En retard'}
                      </span>
                    </div>
                  ))}
                </div>
                <div style={{
                  marginTop: '0.75rem', padding: '0.75rem', background: 'var(--primary-50)',
                  borderRadius: 'var(--radius-sm)', display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                }}>
                  <span style={{ fontSize: '0.875rem', color: 'var(--gray-700)' }}>
                    Déjà facturé : <strong>{pourcentageDejaFacture}%</strong> soit {formatMontant(montantDejaFacture)}
                  </span>
                  <span style={{ fontSize: '0.875rem', color: 'var(--gray-700)' }}>
                    Reste : <strong>{formatMontant(selectedAffaire.montantHT - montantDejaFacture)}</strong>
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Type de facture */}
          <div className="form-section">
            <h2><Receipt size={20} /> Type de facture</h2>
            <div className="form-group">
              <label>Type de facture</label>
              <select
                value={typeFacture}
                onChange={(e) => setTypeFacture(e.target.value)}
                style={{ fontSize: '0.9375rem' }}
              >
                <option value="acompte">Acompte</option>
                <option value="intermediaire">Intermédiaire</option>
                <option value="solde">Solde</option>
              </select>
            </div>
          </div>

          {/* Informations de la facture */}
          <div className="form-section">
            <h2><FileText size={20} /> Informations de la facture</h2>
            <div className="form-row">
              <div className="form-group">
                <label>Numéro de facture</label>
                <input
                  type="text"
                  value={numeroFacture}
                  onChange={(e) => setNumeroFacture(e.target.value)}
                  placeholder="Ex: FC-2025-001"
                  style={{ fontFamily: 'var(--font-mono)' }}
                />
              </div>
              <div className="form-group">
                <label>Date d'envoi</label>
                <input
                  type="date"
                  value={dateEnvoi}
                  onChange={(e) => setDateEnvoi(e.target.value)}
                />
              </div>
            </div>
            <div className="form-group">
              <label>Pièce jointe (facture PDF)</label>
              <div style={{
                display: 'flex', alignItems: 'center', gap: '0.75rem',
                padding: '1rem', border: '2px dashed var(--gray-300)',
                borderRadius: 'var(--radius)', background: 'var(--gray-50)',
                cursor: 'pointer', transition: 'all 0.15s'
              }}
              onClick={() => document.getElementById('file-input').click()}
              onDragOver={(e) => { e.preventDefault(); e.currentTarget.style.borderColor = 'var(--primary)'; }}
              onDragLeave={(e) => { e.currentTarget.style.borderColor = 'var(--gray-300)'; }}
              onDrop={(e) => {
                e.preventDefault();
                e.currentTarget.style.borderColor = 'var(--gray-300)';
                const file = e.dataTransfer.files[0];
                if (file && file.type === 'application/pdf') {
                  setFichierFacture(file);
                }
              }}
              >
                <input
                  id="file-input"
                  type="file"
                  accept=".pdf,application/pdf"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) setFichierFacture(file);
                  }}
                  style={{ display: 'none' }}
                />
                <Upload size={20} style={{ color: 'var(--primary)' }} />
                <div style={{ flex: 1 }}>
                  {fichierFacture ? (
                    <div>
                      <p style={{ margin: 0, fontWeight: 600, fontSize: '0.875rem' }}>{fichierFacture.name}</p>
                      <p style={{ margin: '0.25rem 0 0', fontSize: '0.75rem', color: 'var(--gray-500)' }}>
                        {(fichierFacture.size / 1024).toFixed(2)} Ko
                      </p>
                    </div>
                  ) : (
                    <div>
                      <p style={{ margin: 0, fontWeight: 600, fontSize: '0.875rem' }}>Cliquez pour sélectionner ou glissez-déposez</p>
                      <p style={{ margin: '0.25rem 0 0', fontSize: '0.75rem', color: 'var(--gray-500)' }}>
                        Format accepté : PDF uniquement
                      </p>
                    </div>
                  )}
                </div>
                {fichierFacture && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setFichierFacture(null); }}
                    style={{
                      padding: '0.25rem 0.5rem', background: 'var(--danger)', color: 'white',
                      border: 'none', borderRadius: 'var(--radius-sm)', cursor: 'pointer',
                      fontSize: '0.75rem', fontWeight: 500
                    }}
                  >
                    Supprimer
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Montant et avancement */}
          <div className="form-section">
            <h2><Euro size={20} /> Montant et avancement</h2>
            <div className="form-row">
              <div className="form-group">
                <label>Pourcentage d'avancement facturé (%)</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={pourcentage}
                  onChange={(e) => setPourcentage(parseInt(e.target.value) || 0)}
                  style={{ fontSize: '0.9375rem' }}
                />
              </div>
            </div>

            {selectedAffaire && (
              <div style={{
                display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem',
                padding: '1.25rem', background: 'var(--gray-50)', borderRadius: 'var(--radius)', marginTop: '1rem'
              }}>
                <div>
                  <p style={{ fontSize: '0.75rem', color: 'var(--gray-500)', margin: '0 0 0.25rem', textTransform: 'uppercase' }}>Montant total</p>
                  <p style={{ margin: 0, fontWeight: 600 }}>{formatMontant(selectedAffaire.montantHT)}</p>
                </div>
                <div>
                  <p style={{ fontSize: '0.75rem', color: 'var(--gray-500)', margin: '0 0 0.25rem', textTransform: 'uppercase' }}>% avancement</p>
                  <p style={{ margin: 0, fontWeight: 600 }}>{pourcentage}%</p>
                </div>
                <div>
                  <p style={{ fontSize: '0.75rem', color: 'var(--gray-500)', margin: '0 0 0.25rem', textTransform: 'uppercase' }}>Déjà facturé</p>
                  <p style={{ margin: 0, fontWeight: 600 }}>{formatMontant(montantDejaFacture)}</p>
                </div>
                <div>
                  <p style={{ fontSize: '0.75rem', color: 'var(--gray-500)', margin: '0 0 0.25rem', textTransform: 'uppercase' }}>Cumulé facturé</p>
                  <p style={{ margin: 0, fontWeight: 600, color: 'var(--primary)' }}>{formatMontant(montantDejaFacture + montantFacture)}</p>
                </div>
              </div>
            )}

            {/* Récapitulatif */}
            {selectedAffaire && (
              <div style={{
                marginTop: '1.5rem', padding: '1.5rem',
                background: 'linear-gradient(135deg, var(--primary-50) 0%, #EFF6FF 100%)',
                borderRadius: 'var(--radius-lg)', border: '2px solid var(--primary-100)'
              }}>
                <h3 style={{ margin: '0 0 1rem', fontSize: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <CheckCircle size={18} style={{ color: 'var(--primary)' }} />
                  Récapitulatif de la facture
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {numeroFacture && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid var(--gray-200)' }}>
                      <span>Numéro de facture</span>
                      <span style={{ fontWeight: 600, fontFamily: 'var(--font-mono)' }}>{numeroFacture}</span>
                    </div>
                  )}
                  {dateEnvoi && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid var(--gray-200)' }}>
                      <span>Date d'envoi</span>
                      <span style={{ fontWeight: 600 }}>{new Date(dateEnvoi).toLocaleDateString('fr-FR')}</span>
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid var(--gray-200)' }}>
                    <span>Société émettrice</span>
                    <span style={{ fontWeight: 600 }}>{societe?.nom}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid var(--gray-200)' }}>
                    <span>Montant HT</span>
                    <span style={{ fontWeight: 600 }}>{formatMontant(montantFacture)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid var(--gray-200)' }}>
                    <span>TVA (20%)</span>
                    <span>{formatMontant(montantFacture * 0.2)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.75rem 0', borderTop: '2px solid var(--primary)', marginTop: '0.25rem' }}>
                    <span style={{ fontSize: '1.125rem', fontWeight: 700 }}>Total TTC</span>
                    <span style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--primary)' }}>{formatMontant(montantTTC)}</span>
                  </div>
                  {fichierFacture && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', marginTop: '0.5rem', borderTop: '1px solid var(--gray-200)' }}>
                      <span>Pièce jointe</span>
                      <span style={{ fontWeight: 600, fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                        <FileText size={14} /> {fichierFacture.name}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Informations sur le devis (JOVAL CONSEIL) */}
          {selectedAffaire && societe?.id === 2 && (
            <div className="form-section">
              <div style={{
                padding: '1rem', background: '#FEF3C7', borderRadius: 'var(--radius)',
                display: 'flex', gap: '0.75rem', alignItems: 'flex-start'
              }}>
                <Info size={18} style={{ color: 'var(--warning)', flexShrink: 0, marginTop: '0.125rem' }} />
                <div>
                  <p style={{ margin: '0 0 0.25rem', fontWeight: 600, color: 'var(--warning-dark)' }}>JOVAL CONSEIL — Rappel</p>
                  <p style={{ margin: 0, fontSize: '0.8125rem', color: 'var(--warning-dark)' }}>
                    L'intégralité des précédentes factures doit être indiquée sur cette facture.
                    Les acomptes sont facturés à 50% à la commande. Vérifier le statut réel des paiements.
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="form-actions">
            <Link to="/facturation" className="btn btn-secondary">Annuler</Link>
            <button className="btn btn-primary"><Save size={18} /> Enregistrer la facture</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FactureForm;
